import { formulationRatios } from '@repo/food-engine'
import { parsePh, parseTemperatures, stepTempTime, type CertFacts, type CertSubject } from './facts'
import type { SubstitutionKey } from './substitutions'
import {
  JANG_INGREDIENT_WORDS,
  PRESERVATIVE_ACID_FACTOR,
  PRESERVATIVE_FREE,
  PRESERVATIVE_TERMS,
  RED_PEPPER_POWDER_WORDS,
  RED_YEAST_WORDS,
  TAR_COLOR_PATTERN,
  TAR_COLOR_WORDS,
  findWord,
  textHasAny,
  type PreservativeKind,
} from './terms'

/**
 * 인증 준비도 코드 검사 (2026-11-01, 설계 3-3·3-4). 규칙 id 하나에 검사 함수 하나. 순수 함수라 0원이고 결정적이다.
 * 검사는 "설계(배합·공정·문구)에서 보이는 것" 만 본다. 공장 시설·서류·심사는 보지 않는다 (그 규칙은 엔진 none).
 *
 * 판정 (CodeVerdict.v)
 *  ok      설계에서 지킨 것이 보인다 → 자동 규칙이면 ✅ (readiness.ts)
 *  fix     설계에서 어긴 것이 보인다 → ❌ (경고 규칙은 ⚠️ 전문가)
 *  docs    걸린 원료를 공장 서류로 증빙해야 한다 → ⚠️ 공장
 *  review  걸린 원료·공정을 전문가가 봐야 한다 → ⚠️ 전문가
 *  unknown 판정에 필요한 값이 설계에 없다 → 규칙의 기본 ⚠️
 *  na      이 제품에는 해당하지 않는다
 * 한 규칙이 판정을 여러 개 낼 수 있다 (예: 마가린은 '식물성 유지', 유화제는 '레시틴' 으로 바꿀 것이 달라서).
 */
export type CodeVerdict =
  | { v: 'ok' | 'unknown' | 'na'; evidence?: string; variant?: string }
  | {
      v: 'fix' | 'docs' | 'review'
      ingredientIds?: string[]
      stepIndex?: number
      hintKey?: SubstitutionKey
      evidence?: string
      variant?: string
      who?: 'design' | 'factory'
      /**
       * 다시 만들어도 고칠 수 없는 ❌ (예: 사용자가 고른 제품 자체가 술). 불합격 규칙이어도 게이트가 다시 만들지 않는다 —
       * 같은 요청으로 세 번 만들어도 같은 결과라 AI 비용만 든다. 준비도에는 ❌ 로 남는다
       */
      noRegenerate?: boolean
    }

type Check = (f: CertFacts) => CodeVerdict[]

const ok = (evidence?: string): CodeVerdict[] => [{ v: 'ok', ...(evidence ? { evidence } : {}) }]
const na: CodeVerdict[] = [{ v: 'na' }]
const unknown = (evidence: string): CodeVerdict[] => [{ v: 'unknown', evidence }]

// ── 할랄: 원료 사전 ───────────────────────────────────────────────

/** 이 규칙에 걸린 원료를 행동(fail·improve·docs·review)과 대체안별로 묶는다. 없으면 noneVerdict */
function halalTermCheck(ruleId: string, noneVerdict: CodeVerdict[]): Check {
  return (f) => {
    const hits = f.halalMatches.filter((m) => m.term.ruleId === ruleId)
    if (hits.length === 0) return noneVerdict
    const groups = new Map<string, typeof hits>()
    for (const hit of hits) {
      const key = `${hit.term.action}|${hit.term.hintKey ?? ''}`
      groups.set(key, [...(groups.get(key) ?? []), hit])
    }
    return [...groups.values()].map((group) => {
      const term = group[0]!.term
      const v = term.action === 'fail' || term.action === 'improve' ? 'fix' : term.action
      return {
        v,
        ingredientIds: [...new Set(group.map((h) => h.ingredientId))],
        ...(term.hintKey ? { hintKey: term.hintKey } : {}),
        ...(term.action === 'improve' ? { who: 'design' as const } : {}),
        ...(term.variant ? { variant: term.variant } : {}),
        evidence: group
          .map((h) => `${h.unit}${h.viaSubstitute ? ' (대체재)' : ''} ← ${h.word}`)
          .join(', '),
      }
    })
  }
}

/**
 * 금지 원료 규칙 (H-ING-01~04): 재료 사전 + 공정 글. 공정에서만 쓰는 술·돼지 원료('맛술 2큰술을 넣고 볶는다')는
 * 자유 글이라 오탐이 있을 수 있어 다시 만들지 않고 전문가 확인(⚠️)으로 남긴다. 걸린 것이 없으면 ✅
 */
function prohibitedCheck(ruleId: string): Check {
  const byIngredient = halalTermCheck(ruleId, [])
  return (f) => {
    const out = [...byIngredient(f)]
    for (const hit of f.halalStepHits.filter((h) => h.term.ruleId === ruleId))
      out.push({
        v: 'review',
        stepIndex: hit.stepIndex,
        ...(hit.term.hintKey ? { hintKey: hit.term.hintKey } : {}),
        evidence: `step ${hit.stepIndex + 1} ← ${hit.word}`,
      })
    return out.length ? out : ok()
  }
}

/**
 * 술 (H-ING-04): 제품 자체가 술이면 원료를 볼 것도 없이 할랄이 아니다 (MUI Fatwa 10/2018 제2부 1항·제3부 1·2항:
 * 에탄올 0.5% 이상 발효 음료는 khamr, khamr 가 든 음료는 하람). 다시 만들어도 고칠 수 없어 게이트는 다시 만들지 않는다
 */
const alcoholIngredients = prohibitedCheck('H-ING-04')
const alcoholCheck: Check = (f) => {
  if (f.isAlcoholicDrink)
    return [
      { v: 'fix', variant: 'drink', evidence: 'product is an alcoholic drink', noRegenerate: true },
    ]
  return alcoholIngredients(f)
}

/** 판매·표시에 쓰는 글 (제품명·한 줄 소개·먹는 법·용기·한 줄 규정 요약·소비자 공정·주의 문구·대체재 꼬리표). 규정 메모(baseNote)는 AI 가 본다 */
function salesCopy(s: CertSubject): string[] {
  return [
    s.product.name,
    s.product.tagline,
    s.product.howToUse ?? '',
    s.product.format,
    s.complianceSummary,
    ...s.plainSteps,
    // 주의 문구는 공유 페이지에도 나간다 (share render). 대체재 꼬리표는 결과 화면 재료 옆 칩이다
    s.caution,
    ...s.ingredients.flatMap((i) => i.substitutes.map((x) => x.tag ?? '')),
  ]
}

const HALAL_WORDS = ['할랄', '하랄', 'halal', 'ハラール', 'ハラル', '清真']
/**
 * 판매 문구에서 할랄 표시가 아닌 말: '비할랄 원료와 분리' 같은 공정 주의, 그리고 프롬프트가 대체재에 달게 한
 * 고정 꼬리표 '할랄 목표' (이 대체재가 할랄이라는 뜻이 아니라 목표에 맞춘 대체라는 뜻 — prompts/certs.ts)
 */
const NOT_A_HALAL_CLAIM =
  /비\s?할랄|non[- ]halal|非ハラール|非清真|할랄\s?목표|halal goal|ハラール目標|清真目标/giu
/** 상표·브랜드명 속 할랄도 표시로 본다 (Codex 1.2). 판매 문구에 할랄이라는 말 자체가 있으면 고칠 것 */
const halalSalesCopy: Check = (f) => {
  const hit = salesCopy(f.subject).find((line) =>
    textHasAny(line.replace(NOT_A_HALAL_CLAIM, ' '), HALAL_WORDS),
  )
  return hit ? [{ v: 'fix', evidence: hit.slice(0, 80) }] : ok()
}

const HACCP_WORDS = ['HACCP', '해썹', '식품안전관리인증', 'ハサップ', '危害分析']
const haccpSalesCopy: Check = (f) => {
  const hit = salesCopy(f.subject).find((line) => textHasAny(line, HACCP_WORDS))
  return hit ? [{ v: 'fix', evidence: hit.slice(0, 80) }] : ok()
}
const HACCP_SITE_WORDS = [
  '적용업소',
  '식품안전관리인증기준적용',
  'HACCP certified facility',
  'HACCP facility',
]
const haccpSiteName: Check = (f) => {
  const hit = salesCopy(f.subject).find((line) => textHasAny(line, HACCP_SITE_WORDS))
  return hit ? [{ v: 'fix', evidence: hit.slice(0, 80) }] : ok()
}

/**
 * 인도네시아 라벨 메모 (H-LBL-03): 인도네시아 할랄 라벨 + 해외 인증서 등록번호 자리. 나라 이름만 적은 메모는 아니다.
 * 메모가 있어도 ✅ 로 두지 않는다 — PP 40/2026 Pasal II 1.b 뒤 라벨 형식(Kepka BPJPH 88/2023)이 그대로인지 미확인이다
 * (halal.md 5-1). 확인되면 review 를 ok 로 바꾼다
 */
const IDN_REG_NUMBER =
  /등록\s?번호|registration (?:number|no)|nomor registrasi|登録番号|登记号|注册号|登记编号/iu
const IDN_HALAL_LABEL =
  /할랄\s?라벨|halal label|label halal|ハラール\s?ラベル|ハラールマーク|清真标签|清真标志/iu
const indonesiaLabel: Check = (f) =>
  IDN_REG_NUMBER.test(f.subject.baseNote) && IDN_HALAL_LABEL.test(f.subject.baseNote)
    ? [{ v: 'review', evidence: 'label note present; label format after PP 40/2026 unconfirmed' }]
    : [{ v: 'fix' }]

/** 말레이시아 라벨 메모 (H-LBL-04): 인증기관 이름을 제품에 표시한다는 메모 (Order 2011 제5(2)항) */
const MYS_BODY_NAME =
  /인증\s?기관\s?(?:이름|명)|certification body(?:'s)? name|name of the certification body|nama badan|認証機関(?:の)?名|认证机构名称|认证机构的名称/iu
const malaysiaLabel: Check = (f) => (MYS_BODY_NAME.test(f.subject.baseNote) ? ok() : [{ v: 'fix' }])

/** 인도네시아: 금지 원료가 남으면 'tidak halal' 표시 대상 (H-MKT-03) */
const indonesiaNonHalal: Check = (f) => {
  const haram = f.halalMatches.filter((m) => m.term.action === 'fail' && !m.viaSubstitute)
  if (f.isAlcoholicDrink) return [{ v: 'fix', evidence: 'product is an alcoholic drink' }]
  return haram.length
    ? [{ v: 'fix', ingredientIds: [...new Set(haram.map((h) => h.ingredientId))] }]
    : ok()
}

/**
 * KMA 1360/2021 의 할랄 인증서 면제 원료로 보이는 것 (H-SRC-08): 가공하지 않았거나 물리 가공만 한 식물(신선·냉동 채소·과일,
 * 통곡물), 도축하지 않은 동물 원료(생우유·신선 달걀·신선·냉동·건조·염장 생선), 광물(소금), 물.
 * 물엿(효소 분해)·메주·누룩(미생물)·변성전분처럼 이름에 없는 가공은 여기 들지 않는다
 */
const EXEMPT_LOOKING =
  /^(?:정제수|물|생수|water|水|소금|천일염|정제염|암염|salt|塩|盐|食盐|생우유|원유|raw milk|신선\s?달걀|달걀|계란|egg|eggs|卵|鸡蛋|쌀|찹쌀|현미|보리|밀|통밀|귀리|rice|barley|oats?|米|大米|糯米|생강|마늘|양파|대파|파|배추|무|당근|감자|고구마|오이|호박|양배추|버섯|표고버섯|다시마|사과|배|레몬|토마토|ginger|garlic|onion|cabbage|radish|carrot|potato|mushroom|kelp|apple|pear|lemon|tomato|生姜|大蒜|洋葱|白菜|萝卜|胡萝卜|土豆|蘑菇|海带|苹果|梨|柠檬|番茄)$/iu
/** 신선·냉동·건조·염장 생선 (도축하지 않는 동물 원료) */
const EXEMPT_FISH =
  /^(?:신선|냉동|건조|염장|말린|생)?\s?(?:고등어|연어|대구|명태|멸치|참치|오징어|새우|생선|fish|salmon|cod|anchov(?:y|ies)|tuna)$/iu

/**
 * 인도네시아 면제 원료 분류 (H-SRC-08). 앱은 면제 여부를 정하지 않는다 — '다른 원료·첨가물을 넣지 않음' 조건은 공장 규격서로만
 * 보인다. 그래서 ✅ 를 내지 않고 늘 ⚠️ 공장으로 둔다: 사전에 걸리지 않았지만 면제 목록처럼 보이지도 않는 원료는 그 id 를 붙여
 * (면제인지, 아니면 할랄 서류가 필요한지 공장·인증기관이 확인), 면제로 보이는 원료만 남으면 id 없이 (정말 무가공인지 확인)
 */
const indonesiaExempt: Check = (f) => {
  const flagged = new Set(f.halalMatches.map((m) => m.ingredientId))
  const free = f.subject.ingredients.filter((i) => !flagged.has(i.id))
  if (free.length === 0) return na
  const exemptLooking = (name: string) => {
    const n = name.normalize('NFKC').trim()
    return EXEMPT_LOOKING.test(n) || EXEMPT_FISH.test(n)
  }
  const unclear = free.filter((i) => !exemptLooking(i.name))
  return [
    {
      v: 'docs',
      who: 'factory',
      ...(unclear.length ? { ingredientIds: unclear.map((i) => i.id) } : {}),
      evidence: `${free.length} without a dictionary hit, ${unclear.length} not exempt-looking`,
    },
  ]
}

/** 점검 뒤 배합을 고쳤으면 다시 확인 (H-DOC-03, HK-PLAN-12) */
const editedSinceCheck: Check = (f) => (f.edited ? [{ v: 'review' }] : na)

// ── 해썹: 의무 품목 판정 (설계 3-5) ─────────────────────────────────

export type HaccpDuty = 'mandatory' | 'voluntary' | 'unclear'

/**
 * 식품위생법 시행규칙 제62조① 의무 품목 판정 [HK-SCOPE-01], 축산물 [HK-SCOPE-06], 레토르트 [HK-SCOPE-07].
 * 공장의 전년도 매출 100억원 이상이면 모든 품목이 의무인데 [HK-SCOPE-02], 그건 공장 서류 항목으로 늘 따로 붙는다.
 */
export function haccpDuty(f: CertFacts): HaccpDuty {
  const s = f.subject
  const text = `${s.product.name} ${s.product.subtype} ${s.product.format}`.normalize('NFKC')
  const has = (re: RegExp) => re.test(text)
  const frozen = s.product.storage === 'frozen' || has(/냉동|frozen|冷凍|冷冻/iu)
  if (f.isRetort) return 'mandatory'
  // 분류와 상관없이 이름·세부유형으로 정해지는 의무 품목 (제62조①1·4·5·9·12의2). 요리 이름이 붙은 양념·가루는 그 품목이 아니다
  // ('떡볶이 소스'·'어묵탕 스프'·'빵가루' 는 떡류·어묵·빵류가 아니다)
  const seasoningLike =
    /소스|양념|시즈닝|스프|육수|베이스|믹스|키트|가루|분말|파우더|sauce|seasoning|soup base|mix|kit|powder|crumb|ソース|調味|调料|酱/iu
  // 떡볶이·떡국·떡갈비, 어묵탕은 떡류·어묵이 아니라 그 요리다 (즉석조리식품 등 — 아래 분류로)
  if (
    !has(seasoningLike) &&
    has(
      /캔디|젤리|사탕|양갱|초콜릿|과자|빵|떡(?!볶이|국|갈비|만둣국)|인절미|송편|절편|백설기|빙과|어묵(?!탕)|어육\s?소시지|순대|candy|gummy|jelly|chocolate|cookie|biscuit|bread|rice cake|mochi|fish cake|キャンディ|グミ|チョコレート|餅|软糖|糖果|巧克力|年糕|鱼糕/iu,
    )
  )
    return 'mandatory'
  // 김치: 배추김치만 의무 (제62조①8). 깍두기·총각·열무처럼 다른 채소면 비의무, 그냥 '김치' 는 무엇인지 몰라 확인
  if (has(/김치|kimchi|キムチ|泡菜/iu)) {
    if (has(/배추|포기|맛김치|막김치|napa|baechu|白菜/iu)) return 'mandatory'
    if (has(/깍두기|총각|열무|갓김치|파김치|오이|소박이|부추|radish|cucumber/iu)) return 'voluntary'
    return 'unclear'
  }
  switch (s.product.categoryId) {
    case 'beverage':
      // 다류(액상차·고형차)·커피류는 제외, 탄산음료·혼합음료·음료베이스는 의무
      if (has(/액상차|고형차|차$|티$|커피|tea|coffee|茶|コーヒー|咖啡/iu)) return 'voluntary'
      return 'mandatory'
    case 'juice':
      return 'mandatory'
    case 'bakery':
      return 'mandatory'
    case 'snacks':
      return has(/견과|바$|nut bar|bar$/iu) ? 'unclear' : 'mandatory'
    case 'dessert':
      if (has(/푸딩|아이스크림|젤라또|pudding|ice cream|gelato/iu)) return 'unclear'
      return 'mandatory'
    case 'noodles':
      return 'mandatory'
    case 'processed-seafood':
      if (has(/어묵|어육|fish cake/iu)) return 'mandatory'
      // 냉동 수산물은 어류·연체류·조미가공품만 의무 (제62조①2). 새우·게·조개 같은 갑각·패류는 목록에 없어 확인
      if (frozen)
        return has(
          /새우|꽃게|대게|킹크랩|게살|조개|패류|갑각|굴|홍합|전복|바지락|가리비|shrimp|prawn|crab|clam|oyster|mussel|scallop|abalone|エビ|カニ|貝|虾|蟹|贝/iu,
        )
          ? 'unclear'
          : 'mandatory'
      return 'voluntary'
    case 'dairy':
    case 'processed-meat':
      return 'mandatory'
    case 'fermented':
      return 'voluntary'
    case 'fruit-vegetable':
      if (has(/주스|juice|파우치|레토르트/iu)) return 'mandatory'
      if (has(/통조림|병조림|canned/iu)) return 'unclear'
      return 'voluntary'
    case 'meal':
      if (has(/밀키트|meal kit/iu)) return 'unclear'
      if (has(/즉석조리/iu) && !has(/순대/iu)) return 'voluntary'
      // 냉동은 피자·만두·면만 의무 (제62조①3). 냉동 볶음밥 같은 것은 즉석조리식품일 수 있어 확인
      if (frozen)
        return has(/피자|만두|면|pizza|dumpling|noodle|ピザ|餃子|麺|披萨|饺子|面/iu)
          ? 'mandatory'
          : 'unclear'
      return 'mandatory'
    case 'sauce':
    case 'soup-base':
    case 'fats-oils':
    case 'grain-legume-nut':
    case 'alcohol':
      return 'voluntary'
    default:
      return 'unclear'
  }
}

/**
 * HK-SCOPE-01 은 '의무 품목이면 HACCP 의무 대상으로 표시한다' 이다. 설계에서 볼 수 있는 것은 baseNote 가 그 판정을 적었는지와
 * 앱 판정과 맞는지뿐이다. 그래서 ✅ 는 baseNote 에 HACCP 와 의무/비의무가 적혀 있고 앱 판정과 같을 때만.
 * 공장이 그 유형으로 HACCP를 받아야 하는 일은 HK-SCOPE-03 (공장 서류) 이 따로 맡는다
 */
const HACCP_NOTE = /HACCP|해썹|ハサップ|危害分析/iu
const VOLUNTARY_NOTE =
  /비의무|의무\s?(?:아님|대상이?\s?아)|voluntary|not mandatory|任意|義務(?:では)?ない|非强制|非强制性|自愿/iu
const MANDATORY_NOTE = /의무|mandatory|required|義務|强制/iu
const haccpScope: Check = (f) => {
  const duty = haccpDuty(f)
  if (duty === 'unclear') return [{ v: 'review', variant: duty }]
  const note = f.subject.baseNote.normalize('NFKC')
  if (!HACCP_NOTE.test(note))
    return [{ v: 'fix', variant: duty, evidence: 'baseNote has no HACCP duty line' }]
  const saysVoluntary = VOLUNTARY_NOTE.test(note)
  const saysMandatory = MANDATORY_NOTE.test(note.replace(VOLUNTARY_NOTE, ' '))
  const matches = duty === 'voluntary' ? saysVoluntary : saysMandatory && !saysVoluntary
  return matches
    ? [{ v: 'ok', variant: duty }]
    : [{ v: 'fix', variant: duty, evidence: `baseNote duty differs from ${duty}` }]
}

/** 축산물 HACCP (HK-SCOPE-06)·레토르트 의무 (HK-SCOPE-07): 설계로는 볼 것이 없고, 공장이 그 업종·유형으로 갖췄는지 서류로 본다 */
const factoryDuty =
  (variant: string): Check =>
  () => [{ v: 'docs', who: 'factory', variant }]

// ── 해썹: 식품공전 개별 기준 (장류·간장·소스) ─────────────────────────

interface PreservativeUse {
  ingredientId: string
  kind: PreservativeKind
  /** 산으로 환산한 배합 농도 g/kg (투입 총량 기준). 단위가 섞여 못 셈 = null */
  gPerKg: number | null
  /** 대체재 이름에서 걸렸는지. 대체재는 같은 양으로 바꿔 넣는다고 보고, 합계에는 넣지 않는다 */
  viaSubstitute: boolean
}

/** 보존료 원료와 그 농도. 대체재도 본다 (대체재로 고르면 같은 배합이 된다) */
export function preservativeUses(s: CertSubject): PreservativeUse[] {
  const { basis } = formulationRatios(s.ingredients)
  const total =
    basis === 'input_mass'
      ? s.ingredients.reduce(
          (sum, i) => sum + i.qty * (i.unit.trim().toLowerCase() === 'kg' ? 1000 : 1),
          0,
        )
      : null
  const uses: PreservativeUse[] = []
  for (const ing of s.ingredients) {
    const names = [ing.name, ...ing.substitutes.map((x) => x.name)]
    for (const [n, name] of names.entries()) {
      // '된장(무보존료)'·'preservative-free gochujang' 은 보존료가 없다는 뜻이다 ('보존료' 라는 말이 들어 있어도)
      if (PRESERVATIVE_FREE.test(name.normalize('NFKC'))) continue
      const kind = (Object.keys(PRESERVATIVE_TERMS) as PreservativeKind[]).find((k) =>
        findWord(name, PRESERVATIVE_TERMS[k]),
      )
      if (!kind) continue
      const unit = ing.unit.trim().toLowerCase()
      const grams = unit === 'g' ? ing.qty : unit === 'kg' ? ing.qty * 1000 : null
      const factorKey = Object.keys(PRESERVATIVE_ACID_FACTOR).find((k) =>
        name.toLowerCase().replace(/\s+/g, '').includes(k.replace(/\s+/g, '')),
      )
      const factor = factorKey ? PRESERVATIVE_ACID_FACTOR[factorKey]! : 1
      uses.push({
        ingredientId: ing.id,
        kind,
        gPerKg: total && grams !== null ? (grams * factor * 1000) / total : null,
        viaSubstitute: n > 0,
      })
    }
  }
  return uses
}

const ids = (uses: { ingredientId: string }[]) => [...new Set(uses.map((u) => u.ingredientId))]
const round = (n: number) => Math.round(n * 100) / 100

/**
 * 계산값이 한도의 80~100% 면 ✅ 로 두지 않는다. 배합 농도는 투입 총량 기준이라, 졸이거나 짜서 물이 빠진 최종 제품은
 * 더 진하다 — 최종 제품 농도는 시험성적서로만 안다
 */
const NEAR_LIMIT = 0.8
const nearLimit = (values: [value: number, limit: number][]) =>
  values.some(([value, limit]) => value > limit * NEAR_LIMIT && value <= limit)
const FINAL_CONCENTRATION = 'final-product concentration needs a lab result'
/** 간장 1 L 의 무게 (kg). 간장 보존료 기준(g/L)으로 바꿀 때 쓴다 */
const SOY_SAUCE_KG_PER_L = 1.2

/** 장류: 소브산류만, 소브산으로서 1.0 g/kg 이하 [HK-STD-01] */
const jangPreservative: Check = (f) => {
  const uses = preservativeUses(f.subject)
  const banned = uses.filter((u) => u.kind !== 'sorbate')
  if (banned.length)
    return [
      {
        v: 'fix',
        ingredientIds: ids(banned),
        hintKey: 'preservative-jang',
        evidence: 'non-sorbate',
      },
    ]
  const sorbate = uses.filter((u) => u.kind === 'sorbate' && !u.viaSubstitute)
  if (sorbate.length === 0) return ok('no preservative')
  const total = sorbate.reduce<number | null>(
    (sum, u) => (sum === null || u.gPerKg === null ? null : sum + u.gPerKg),
    0,
  )
  if (total === null) return unknown('sorbate amount not computable')
  if (nearLimit([[total, 1.0]]))
    return unknown(`sorbate ${round(total)} g/kg — ${FINAL_CONCENTRATION}`)
  return total <= 1.0
    ? ok(`sorbate ${round(total)} g/kg`)
    : [
        {
          v: 'fix',
          ingredientIds: ids(sorbate),
          hintKey: 'preservative-jang',
          evidence: `sorbate ${round(total)} g/kg`,
        },
      ]
}

/** 타르색소가 있는 원료 */
function tarColorIds(s: CertSubject): string[] {
  return s.ingredients
    .filter((i) =>
      [i.name, ...i.substitutes.map((x) => x.name)].some(
        (n) => findWord(n, TAR_COLOR_WORDS) !== null || TAR_COLOR_PATTERN.test(n.normalize('NFKC')),
      ),
    )
    .map((i) => i.id)
}

/** 장류: 타르색소 불검출 [HK-STD-02] */
const jangTarColor: Check = (f) => {
  const hit = tarColorIds(f.subject)
  return hit.length ? [{ v: 'fix', ingredientIds: hit, hintKey: 'tar-color' }] : ok()
}

/** 원료 몇 개의 배합 비율 합 (%). 같은 차원이 아니면 null */
function shareOf(s: CertSubject, words: readonly string[]): number | null {
  const { basis, ratios } = formulationRatios(s.ingredients)
  if (!basis) return null
  let sum = 0
  s.ingredients.forEach((ing, i) => {
    if (findWord(ing.name, words)) sum += ratios[i] ?? 0
  })
  return sum
}

/** 고추장: 고춧가루 6% 이상 [HK-STD-03] */
const gochujangPepper: Check = (f) => {
  const share = shareOf(f.subject, RED_PEPPER_POWDER_WORDS)
  if (share === null) return unknown('ratio basis mixed')
  return share >= 6
    ? ok(`red pepper ${round(share)}%`)
    : [{ v: 'fix', evidence: `red pepper ${round(share)}%` }]
}

/** 고추장: 홍국색소 금지 [HK-STD-04] */
const gochujangRedYeast: Check = (f) => {
  const hit = f.subject.ingredients
    .filter((i) =>
      [i.name, ...i.substitutes.map((x) => x.name)].some((n) => findWord(n, RED_YEAST_WORDS)),
    )
    .map((i) => i.id)
  return hit.length ? [{ v: 'fix', ingredientIds: hit, hintKey: 'red-yeast' }] : ok()
}

/** 혼합장: 장류 50% 이상 [HK-STD-05] */
const mixedJangShare: Check = (f) => {
  const share = shareOf(f.subject, JANG_INGREDIENT_WORDS)
  if (share === null) return unknown('ratio basis mixed')
  return share >= 50
    ? ok(`jang ${round(share)}%`)
    : [{ v: 'fix', evidence: `jang ${round(share)}%` }]
}

/** 간장: 안식향산류 0.6 g/L·파라옥시안식향산류 0.25 이하(합계 0.6), 그 밖의 보존료 없음 [HK-STD-06] */
const soyPreservative: Check = (f) => {
  const uses = preservativeUses(f.subject)
  const banned = uses.filter((u) => u.kind !== 'benzoate' && u.kind !== 'paraben')
  if (banned.length)
    return [
      {
        v: 'fix',
        ingredientIds: ids(banned),
        hintKey: 'preservative-soy',
        evidence: 'not benzoate/paraben',
      },
    ]
  const main = uses.filter((u) => !u.viaSubstitute)
  if (main.length === 0) return ok('no preservative')
  // 간장 기준은 g/L 이다. 간장은 1 L 가 약 1.2 kg 이라 g/kg × 1.2 = g/L 로 바꾼다. 못 세면 확인 필요
  if (main.some((u) => u.gPerKg === null)) return unknown('preservative amount not computable')
  const sum = (k: PreservativeKind) =>
    main.filter((u) => u.kind === k).reduce((a, u) => a + u.gPerKg! * SOY_SAUCE_KG_PER_L, 0)
  const benzoate = sum('benzoate')
  const paraben = sum('paraben')
  const within = benzoate <= 0.6 && paraben <= 0.25 && benzoate + paraben <= 0.6
  if (
    within &&
    nearLimit([
      [benzoate, 0.6],
      [paraben, 0.25],
      [benzoate + paraben, 0.6],
    ])
  )
    return unknown(
      `benzoate ${round(benzoate)}, paraben ${round(paraben)} g/L — ${FINAL_CONCENTRATION}`,
    )
  return within
    ? ok(`benzoate ${round(benzoate)}, paraben ${round(paraben)}`)
    : [
        {
          v: 'fix',
          ingredientIds: ids(uses),
          hintKey: 'preservative-soy',
          evidence: `benzoate ${round(benzoate)}, paraben ${round(paraben)}`,
        },
      ]
}

const KETCHUP = /케첩|케찹|ketchup|ケチャップ|番茄酱/iu
const MAYONNAISE = /마요네즈|mayonnaise|マヨネーズ|蛋黄酱/iu

/**
 * 소스 [HK-STD-07, 식품공전 제5.13-2]: 소브산류 1.0 g/kg·파라옥시안식향산류 0.2 이하(합계 1.0), 그 밖의 보존료 없음.
 * 토마토케첩은 소브산 0.5 g/kg. 마요네즈는 따로 정한 기준(소브산류 1.0·병용 1.5, 안식향산류 허용)이라 소브산 1.0 넘음만 ❌ 로
 * 보고 그 밖의 보존료는 확인 필요 (안식향산 한도 수치는 팩에서 확인하지 못했다). 타르색소는 허용 목록이 있어 확인 필요
 */
const saucePreservative: Check = (f) => {
  const uses = preservativeUses(f.subject)
  const text = `${f.subject.product.name} ${f.subject.product.subtype}`.normalize('NFKC')
  const mayo = MAYONNAISE.test(text)
  const sorbateLimit = KETCHUP.test(text) ? 0.5 : 1.0
  const banned = uses.filter((u) => u.kind !== 'sorbate' && u.kind !== 'paraben')
  if (banned.length && !mayo)
    return [
      {
        v: 'fix',
        ingredientIds: ids(banned),
        hintKey: 'preservative-sauce',
        evidence: 'not sorbate/paraben',
      },
    ]
  const main = uses.filter((u) => !u.viaSubstitute)
  if (main.some((u) => u.gPerKg === null)) return unknown('preservative amount not computable')
  const sum = (k: PreservativeKind) =>
    main.filter((u) => u.kind === k).reduce((a, u) => a + u.gPerKg!, 0)
  const sorbate = sum('sorbate')
  const paraben = sum('paraben')
  const evidence = `sorbate ${round(sorbate)}, paraben ${round(paraben)} g/kg`
  const fix: CodeVerdict[] = [
    { v: 'fix', ingredientIds: ids(uses), hintKey: 'preservative-sauce', evidence },
  ]
  if (mayo) {
    if (sorbate > 1.0) return fix
    if (main.some((u) => u.kind !== 'sorbate'))
      return unknown(`mayonnaise — separate limits for ${evidence}`)
  } else if (!(sorbate <= sorbateLimit && paraben <= 0.2 && sorbate + paraben <= 1.0)) return fix
  if (
    nearLimit([
      [sorbate, sorbateLimit],
      [paraben, 0.2],
      [sorbate + paraben, 1.0],
    ])
  )
    return unknown(`${evidence} — ${FINAL_CONCENTRATION}`)
  if (tarColorIds(f.subject).length)
    return unknown('tar colour — check the permitted list for sauces')
  return ok()
}

/** 장류·소스 + 할랄: 주정 충돌 [HK-STD-08] */
const ethanolWithHalal: Check = (f) => {
  const hits = f.halalMatches.filter((m) => m.term.ruleId === 'H-ALC-01')
  return hits.length
    ? [
        {
          v: 'review',
          ingredientIds: [...new Set(hits.map((h) => h.ingredientId))],
          hintKey: 'alcohol-ethanol',
        },
      ]
    : ok()
}

/** 레토르트: 보존료 일절 금지, 타르색소 불검출 [HK-CCP-05, 식품공전 제4.2] */
const retortPreservative: Check = (f) => {
  const uses = preservativeUses(f.subject)
  const tar = tarColorIds(f.subject)
  const out: CodeVerdict[] = []
  if (uses.length) out.push({ v: 'fix', ingredientIds: ids(uses), hintKey: 'preservative-retort' })
  if (tar.length) out.push({ v: 'fix', ingredientIds: tar, hintKey: 'tar-color' })
  return out.length ? out : ok()
}

// ── 해썹: 공정 (가열·금속검출·냉각) ─────────────────────────────────

/** 63℃ 30분과 같은 효과인지 (z = 10℃ 로 환산. 영양세포 병원균의 z 값 4~7℃ 보다 커서 높은 온도에 점수를 덜 준다 — 보수적) */
export function pasteurizationEquivalent(tempC: number, minutes: number): number {
  return minutes * 10 ** ((tempC - 63) / 10)
}

/** 121.1℃ 기준 F0 (z = 10℃). 120℃ 4분 ≈ F0 3.1 */
export function sterilizationF0(tempC: number, minutes: number): number {
  return minutes * 10 ** ((tempC - 121.1) / 10)
}

const PASTEURIZE_STEP = /살균|열간충전|핫필|pasteur|hot.?fill|殺菌|杀菌|巴氏/iu
const STERILIZE_STEP = /레토르트|멸균|고압|retort|steril|autoclave|canning|レトルト|滅菌|灭菌/iu
const stepText = (st: CertSubject['steps'][number]) =>
  `${st.title} ${st.desc} ${st.equipment} ${st.qc}`

/** 표기로 고른 단계, 없으면 가장 뜨거운 가열 단계 */
function pickHeatStep(f: CertFacts, re: RegExp): number | null {
  const named = f.subject.steps.findIndex((st) => re.test(stepText(st)))
  if (named >= 0) return named
  const hottest = [...f.heatSteps].sort((a, b) => (b.tempC ?? 0) - (a.tempC ?? 0))[0]
  return hottest ? hottest.index : null
}

/** 그 온도가 제품 중심(품온)이라는 표시. 식품공전 기준은 '중심부' 온도라 솥·물 온도만 적은 단계는 판정하지 않는다 */
const CORE_TEMP = /중심|품온|core|cent(?:re|er)|中心|芯温|品温/iu

/**
 * 살균: 중심부 63℃ 30분 이상 또는 동등 이상 [HK-CCP-01]. ✅ 는 중심온도 63℃ 이상·30분 이상을 그대로 적었을 때만.
 * '85℃ 15초' 같은 동등 효력 환산은 팩이 채택하지 않았다 (haccp.md 6-7: 공식 근거 없음) — 공장 유효성 평가로 정해서 ⚠️
 */
const pasteurization: Check = (f) => {
  const i = pickHeatStep(f, PASTEURIZE_STEP)
  if (i === null) return [{ v: 'fix', evidence: 'no heat step' }]
  const step = f.subject.steps[i]!
  const { tempC, minutes } = stepTempTime(step)
  if (tempC === null || minutes === null)
    return [{ v: 'fix', stepIndex: i, evidence: 'temp/time not numeric' }]
  if (tempC < 63) return [{ v: 'fix', stepIndex: i, evidence: `${tempC}℃ < 63℃` }]
  if (minutes < 30)
    return unknown(`${tempC}℃ ${round(minutes)} min — equivalence needs factory validation`)
  return CORE_TEMP.test(`${step.temp} ${step.qc} ${step.desc}`)
    ? ok(`core ${tempC}℃ ${round(minutes)} min`)
    : unknown(`${tempC}℃ ${round(minutes)} min — core temperature not stated`)
}

/** 식품공전 멸균 기준 '120℃ 4분' 의 F0 (z=10, 약 3.10). 업계에서 흔한 'F0 3' 은 이보다 낮다 (haccp.md G11) */
export const STERILIZATION_F0_MIN = sterilizationF0(120, 4)

/**
 * 멸균: 밀봉 후 중심부 120℃ 4분 이상 또는 동등 이상 [HK-CCP-02]. ✅ 는 중심온도 120℃ 이상·4분 이상을 그대로 적었을 때만.
 * 다른 온도·시간이나 F0 로 적은 '동등 이상' 은 공장 유효성 평가로 정해서 ⚠️, 공전 기준 F0 에도 못 미치면 ❌
 */
const sterilization: Check = (f) => {
  const i = pickHeatStep(f, STERILIZE_STEP)
  if (i === null) return [{ v: 'fix', evidence: 'no sterilization step' }]
  const step = f.subject.steps[i]!
  const { tempC, minutes } = stepTempTime(step)
  const core = CORE_TEMP.test(`${step.temp} ${step.qc} ${step.desc}`)
  if (tempC !== null && minutes !== null && tempC >= 120 && minutes >= 4)
    return core
      ? ok(`core ${tempC}℃ ${round(minutes)} min`)
      : unknown(`${tempC}℃ ${round(minutes)} min — core temperature not stated`)
  const stated = `${step.qc} ${step.desc} ${step.time}`
    .normalize('NFKC')
    .match(/F\s?0\s*(?:값)?\s*(?:≥|>=|=|:)?\s*(\d+(?:\.\d+)?)/iu)
  const computed = tempC !== null && minutes !== null ? sterilizationF0(tempC, minutes) : null
  const f0 = Math.max(stated ? Number(stated[1]) : 0, computed ?? 0)
  if (f0 >= STERILIZATION_F0_MIN)
    return unknown(`F0 ${round(f0)} — equivalence needs factory validation`)
  if (tempC === null || minutes === null)
    return [{ v: 'fix', stepIndex: i, evidence: 'temp/time not numeric' }]
  return [{ v: 'fix', stepIndex: i, evidence: `F0 ${round(f0)} < ${round(STERILIZATION_F0_MIN)}` }]
}

/** pH 4.6 이하 산성식품은 살균으로 제조 가능. estPh 숫자와 pH 측정 qc 가 근거 [HK-CCP-03] */
const acidPasteurized: Check = (f) => {
  const ph = parsePh(f.subject.estPh)
  const measured = f.subject.steps.some((st) => /pH/i.test(st.qc))
  if (ph === null) return [{ v: 'fix', evidence: 'estPh not numeric' }]
  if (ph > 4.6) return [{ v: 'fix', evidence: `pH ${ph} > 4.6` }]
  return measured ? ok(`pH ${ph}`) : [{ v: 'fix', evidence: 'no pH measurement in qc' }]
}

/**
 * 로트(제조 기호) 표시 자리 [HK-PRP-17, HK-CCP-04]. 코드처럼 쓰는 말만 본다 — '제조일로부터 12개월'(소비기한 글)이나
 * 'lotus root'·'a lot of' 는 로트 표시가 아니다
 */
const LOT_MARK =
  /로트|제조\s?번호|배치\s?번호|제조\s?일자\s?(?:표시|기호|자리|코드)|(?:^|[^a-z])lot\s*(?:no\.?|number|code|#|:|번호)|(?:^|[^a-z])batch\s*(?:code|number|no\.?)|ロット|製造番号|製造記号|批号|批次号|生产批次/iu
const lotMarking: Check = (f) =>
  LOT_MARK.test(
    `${f.subject.baseNote} ${f.subject.steps.map((s) => s.qc).join(' ')}`.normalize('NFKC'),
  )
    ? ok()
    : [{ v: 'fix' }]

/** 유가공품 살균: 63~65℃ 30분 / 72~75℃ 15~20초 / 130~150℃ 0.5~5초 또는 동등 이상, 살균 뒤 바로 10℃ 이하 [HK-CCP-06] */
const dairyPasteurization: Check = (f) => {
  const i = pickHeatStep(f, PASTEURIZE_STEP)
  if (i === null) return [{ v: 'fix', evidence: 'no heat step' }]
  const { tempC, minutes } = stepTempTime(f.subject.steps[i]!)
  if (tempC === null || minutes === null)
    return [{ v: 'fix', stepIndex: i, evidence: 'temp/time not numeric' }]
  const meets =
    (tempC >= 63 && minutes >= 30) ||
    (tempC >= 72 && minutes >= 0.25) ||
    (tempC >= 130 && minutes >= 0.5 / 60)
  if (!meets) return [{ v: 'fix', stepIndex: i, evidence: `${tempC}℃ ${round(minutes)} min` }]
  const cooled = f.subject.steps
    .slice(i + 1)
    .some((st) => parseTemperatures(`${st.temp} ${st.qc}`).some((t) => t <= 10))
  return cooled ? ok() : [{ v: 'fix', stepIndex: i, evidence: 'no cooling to 10℃ or below' }]
}

const METAL_WORDS = [
  '금속검출',
  '금속 검출',
  '금속탐지',
  '금속이물',
  '엑스레이',
  'x-ray',
  'x선',
  'metal detect',
  'metal detector',
  '金属検出',
  '金属探知',
  '金属检测',
  '金属探测',
]
/**
 * 포장 뒤 금속검출 단계, 완제품 2 mm 이상 금속이물 불검출 [HK-CCP-07, 식품공전 제2.3]. 적은 mm 값이 모두 2 mm 이하여야 ✅.
 * Fe·SUS 테스트피스 크기는 국내 공식 기준을 찾지 못해(haccp.md 5-5) 따로 요구하지 않는다
 */
const metalDetection: Check = (f) => {
  const i = f.subject.steps.findIndex((st) => textHasAny(stepText(st), METAL_WORDS))
  if (i < 0) return [{ v: 'fix', evidence: 'no metal detection step' }]
  const step = f.subject.steps[i]!
  const mm = [
    ...`${step.qc} ${step.desc}`.normalize('NFKC').matchAll(/(\d+(?:\.\d+)?)\s*mm/giu),
  ].map((m) => Number(m[1]))
  if (mm.length === 0) return [{ v: 'fix', stepIndex: i, evidence: 'no sensitivity in mm' }]
  return mm.every((v) => v <= 2.0)
    ? ok(`${mm.join(', ')} mm`)
    : [{ v: 'fix', stepIndex: i, evidence: 'sensitivity > 2 mm' }]
}

const MAGNET_WORDS = ['자석', '마그넷', '자력', 'magnet', '磁石', 'マグネット', '磁铁', '除铁']
/** 분말을 금속 분쇄기로 갈면 최종 분쇄 뒤 자석 [HK-CCP-08] */
const magnetAfterGrinding: Check = (f) =>
  f.subject.steps.some((st) => textHasAny(stepText(st), MAGNET_WORDS)) ? ok() : [{ v: 'fix' }]

const COOL_WORDS = ['냉각', '식힘', '식혀', 'cool', 'chill', '冷却', '冷やす', '降温']
/** 가열 다음 냉각 단계에 목표 온도 (국가 공통 수치 없음, 경고) [HK-CCP-10] */
const coolingTarget: Check = (f) => {
  const lastHeat = Math.max(...f.heatSteps.map((h) => h.index))
  const later = f.subject.steps.slice(lastHeat)
  const cooling = later.findIndex((st) => textHasAny(stepText(st), COOL_WORDS))
  if (cooling < 0) return [{ v: 'fix', evidence: 'no cooling after heating' }]
  const step = later[cooling]!
  return parseTemperatures(`${step.temp} ${step.qc} ${step.desc}`).some((t) => t < 60)
    ? ok()
    : [{ v: 'fix', stepIndex: lastHeat + cooling, evidence: 'cooling without target temperature' }]
}

/** 냉동 원료 해동: 별도 해동 공간, 물 해동이면 21℃ 이하 [HK-CCP-11] */
const thawing: Check = (f) => {
  const i = f.subject.steps.findIndex((st) => /해동|thaw|defrost|解凍|解冻/iu.test(stepText(st)))
  if (i < 0) return unknown('frozen raw material without a thawing step')
  const step = f.subject.steps[i]!
  const temps = parseTemperatures(`${step.temp} ${step.qc} ${step.desc}`)
  return temps.length && temps.every((t) => t <= 21) ? ok() : [{ v: 'fix', stepIndex: i }]
}

// ── 해썹: 보관 온도 ───────────────────────────────────────────────

const STORAGE_WORDS = [
  '보관',
  '유통',
  '저장',
  'storage',
  'store',
  'keep',
  'distribut',
  '保存',
  '保管',
  '流通',
  '储存',
  '贮存',
  '保存温度',
]
const SKIP_SENTENCE = /개봉|해동|after opening|once opened|thaw|開封|解凍|开封|解冻/iu
const NEGATION = /금지|말 ?것|않|피하|no |not |do not|avoid|禁止|しない|避け|不要|不可|避免/iu

/** baseNote·caution·마지막 단계에서 보관을 말하는 문장 (개봉 후·해동 문장은 뺀다) */
function storageSentences(s: CertSubject): string[] {
  const last = s.steps[s.steps.length - 1]
  return [s.baseNote, s.caution, last ? stepText(last) : '']
    .join('\n')
    .split(/[.。\n!?]/u)
    .map((x) => x.trim())
    .filter((x) => x && textHasAny(x, STORAGE_WORDS) && !SKIP_SENTENCE.test(x))
}

/**
 * 식품공전 보관 온도(실온 1~35℃, 냉장 0~10℃, 냉동 -18℃ 이하)와 메모 속 보관 온도가 맞는지 [HK-STO-01, HK-PRP-08].
 * 샐러드·훈제연어·액란·신선편의식품·가금육은 냉장 5℃ 이하 (식품공전 2.4-2, 별표1 I-33). 보관 온도를 숫자로 적지 않았으면
 * 볼 것이 없어 확인 필요다 (✅ 아님)
 */
const storageTemperature: Check = (f) => {
  const storage = f.subject.product.storage
  const temps = storageSentences(f.subject).flatMap(parseTemperatures)
  if (temps.length === 0) return unknown('no storage temperature stated')
  const chilledMax = f.chill5Type ? 5 : 10
  const bad =
    storage === 'refrigerated'
      ? temps.filter((t) => t > chilledMax && t < 50)
      : storage === 'frozen'
        ? temps.filter((t) => t > -18 && t < 50)
        : temps.filter((t) => t > 35 && t < 50)
  return bad.length ? [{ v: 'fix', evidence: `${storage} but ${bad.join(', ')}℃` }] : ok()
}

/** 상온 7일 미만 보존이면 냉장·냉동 (경고) [HK-STO-02] */
const shortAmbient: Check = (f) =>
  f.subject.product.storage === 'ambient' && f.subject.product.shelfLifeDays < 7
    ? [{ v: 'fix', evidence: `ambient ${f.subject.product.shelfLifeDays} days` }]
    : ok()

/** 유통 온도대가 메모와 엇갈리지 않는지: 실온 제품인데 냉장 유통, 냉장 제품인데 냉동 유통 [HK-STO-03] */
const storageConflict: Check = (f) => {
  const storage = f.subject.product.storage
  const sentences = storageSentences(f.subject).filter((x) => !NEGATION.test(x))
  const cold = /냉장|refrigerat|chill|冷蔵|冷藏/iu
  const frozen = /냉동|frozen|freez|冷凍|冷冻/iu
  const hit =
    storage === 'ambient'
      ? sentences.find((x) => cold.test(x) || frozen.test(x))
      : storage === 'refrigerated'
        ? sentences.find((x) => frozen.test(x))
        : undefined
  return hit ? [{ v: 'fix', evidence: hit.slice(0, 80) }] : ok()
}

/** 실온 유통 금지 유형 (양념젓갈·가공두부·두유·어육가공품·발효유·유가공품 …, facts.ts 의 coldChainType). 멸균 제품은 예외 [HK-STO-04] */
const coldChainRequired: Check = (f) =>
  f.subject.product.storage === 'ambient' && !f.isSterilized
    ? [{ v: 'fix', evidence: 'ambient cold-chain type' }]
    : ok()

// ── 해썹: 계획 문서 ───────────────────────────────────────────────

/** 제품설명서 필수 칸: 배합비율이 같은 단위로 셀 수 있어야 한다 [HK-PLAN-02] */
const productDescription: Check = (f) =>
  formulationRatios(f.subject.ingredients).basis
    ? ok()
    : [{ v: 'fix', evidence: 'ratio basis mixed' }]

/** CCP(가열·금속검출) 단계의 qc 에 한계기준 수치가 있는지 [HK-PLAN-05] */
const ccpLimits: Check = (f) => {
  const ccp = f.subject.steps
    .map((st, i) => ({ st, i }))
    .filter(
      ({ st, i }) =>
        f.heatSteps.some((h) => h.index === i) || textHasAny(stepText(st), METAL_WORDS),
    )
  if (ccp.length === 0) return unknown('no CCP step')
  const missing = ccp.find(({ st }) => !/\d/.test(st.qc))
  return missing ? [{ v: 'fix', stepIndex: missing.i }] : ok()
}

/** 규칙 id → 코드 검사. 여기 없는 코드 엔진 규칙은 없다 (테스트가 rules.ts 와 대조한다) */
export const CODE_CHECKS: Record<string, Check> = {
  // 할랄
  'H-ING-01': prohibitedCheck('H-ING-01'),
  'H-ING-02': prohibitedCheck('H-ING-02'),
  'H-ING-03': prohibitedCheck('H-ING-03'),
  'H-ING-04': alcoholCheck,
  'H-ING-06': halalTermCheck('H-ING-06', na),
  'H-ALC-01': halalTermCheck('H-ALC-01', na),
  'H-ALC-04': halalTermCheck('H-ALC-04', na),
  'H-SRC-01': halalTermCheck('H-SRC-01', na),
  'H-SRC-02': halalTermCheck('H-SRC-02', na),
  'H-SRC-03': halalTermCheck('H-SRC-03', na),
  'H-SRC-04': halalTermCheck('H-SRC-04', na),
  'H-SRC-05': halalTermCheck('H-SRC-05', na),
  'H-SRC-06': halalTermCheck('H-SRC-06', na),
  'H-SRC-07': halalTermCheck('H-SRC-07', na),
  'H-SRC-08': indonesiaExempt,
  'H-MKT-03': indonesiaNonHalal,
  'H-LBL-01': halalSalesCopy,
  'H-LBL-03': indonesiaLabel,
  'H-LBL-04': malaysiaLabel,
  'H-DOC-03': editedSinceCheck,
  // 해썹
  'HK-SCOPE-01': haccpScope,
  'HK-SCOPE-06': factoryDuty('livestock'),
  'HK-SCOPE-07': factoryDuty('retort'),
  'HK-STD-01': jangPreservative,
  'HK-STD-02': jangTarColor,
  'HK-STD-03': gochujangPepper,
  'HK-STD-04': gochujangRedYeast,
  'HK-STD-05': mixedJangShare,
  'HK-STD-06': soyPreservative,
  'HK-STD-07': saucePreservative,
  'HK-STD-08': ethanolWithHalal,
  'HK-CCP-01': pasteurization,
  'HK-CCP-02': sterilization,
  'HK-CCP-03': acidPasteurized,
  'HK-CCP-04': lotMarking,
  'HK-CCP-05': retortPreservative,
  'HK-CCP-06': dairyPasteurization,
  'HK-CCP-07': metalDetection,
  'HK-CCP-08': magnetAfterGrinding,
  'HK-CCP-10': coolingTarget,
  'HK-CCP-11': thawing,
  'HK-STO-01': storageTemperature,
  'HK-STO-02': shortAmbient,
  'HK-STO-03': storageConflict,
  'HK-STO-04': coldChainRequired,
  'HK-PRP-08': storageTemperature,
  'HK-PRP-17': lotMarking,
  'HK-PLAN-02': productDescription,
  'HK-PLAN-05': ccpLimits,
  'HK-PLAN-12': editedSinceCheck,
  'HK-LBL-01': haccpSalesCopy,
  'HK-LBL-02': haccpSiteName,
}
